import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Moon, Sun } from 'lucide-react';
import AuthBrandLogo from '../components/AuthBrandLogo';
import { useTheme } from '../contexts/ThemeContext';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import logoIconLight from '../assets/logo-icon.png';
import logoIconDark from '../assets/logo-icon-dark.png';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function VerifyEmailPage() {
  const query = useQuery();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { establishSession, getPostLoginPath } = useAuth();
  const logoIconSrc = theme === 'dark' ? logoIconDark : logoIconLight;

  const email = query.get('email') || '';
  const verificationId = query.get('verification_id') || '';

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const onResend = async () => {
    if (!email || !verificationId) {
      toast.error('Missing verification details. Please log in again.');
      return;
    }
    setResending(true);
    try {
      const res = await axios.post(`${API_URL}/auth/resend-verify-email-otp`, {
        email,
        verification_id: verificationId,
      });
      if (res.data?.email_sent === false) {
        toast.error('Could not send email. Check Resend configuration on the server.');
      } else {
        toast.success('A new code was sent to your email.');
      }
    } catch {
      toast.error('Could not resend code. Try again in a moment.');
    } finally {
      setResending(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !verificationId) {
      toast.error('Missing verification details. Please register or login again.');
      return;
    }
    if (!code.trim()) {
      toast.error('Enter the code from your email');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/verify-email-otp`, {
        email,
        verification_id: verificationId,
        code: code.trim(),
      });
      const { access_token, user: userData } = res.data || {};
      if (!access_token || !userData) {
        toast.error('Verification failed. Please try again.');
        return;
      }
      establishSession(access_token, userData);
      toast.success('Email verified. Welcome!');
      navigate(getPostLoginPath(userData), { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950">
      <header className="flex items-center justify-between px-6 py-4">
        <AuthBrandLogo />
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          data-testid="theme-toggle"
          className="text-slate-600 dark:text-slate-400"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-slide-up">
          <Card className="border-slate-200 dark:border-slate-800 shadow-lg">
            <CardHeader className="space-y-1 text-center">
              <img src={logoIconSrc} alt="DocTree" className="mx-auto h-12 w-12 mb-2" />
              <CardTitle className="text-2xl font-bold font-[Manrope] text-slate-900 dark:text-white">
                Verify your email
              </CardTitle>
              <CardDescription className="text-slate-600 dark:text-slate-400">
                Enter the 6-digit code sent to <span className="font-medium">{email}</span>.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-slate-700 dark:text-slate-300">
                    Verification code
                  </Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    data-testid="verify-email-code-input"
                    className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-center tracking-[0.4em]"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={loading}
                  data-testid="verify-email-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-sm"
                  disabled={resending || loading}
                  onClick={onResend}
                >
                  {resending ? 'Sending…' : 'Resend code'}
                </Button>
              </div>
              <div className="mt-4 text-center text-sm">
                <Link
                  to="/login"
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                >
                  Back to login
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <footer className="py-4 text-center text-sm text-slate-500 dark:text-slate-500">
        <p>&copy; {new Date().getFullYear()} DocTree. All rights reserved.</p>
      </footer>
    </div>
  );
}

